// src/utils/wanPriority.js
//
// SINGLE SOURCE OF TRUTH for WAN priority read/write logic. Used by:
//   - components/LiveWanPanel.jsx          (Site Dashboard live panel)
//   - components/monitoring/WanPriorityControl.js (Monitoring page WAN table)
// Both screens MUST go through checkPriorityChange() before sending
// anything - never build a /api/wan/priority payload anywhere else.
//
// The functions below were moved here verbatim from LiveWanPanel.jsx
// (already tested against legacy WanTable.jsx), not reimplemented.

import { fetchAllWanFM, fetchAllWanManaged } from './routerLiveData';

export const OFFLINE_STATUS_PATTERNS = ['disconnected', 'offline', 'down', 'no cable', 'no device', 'failed', 'unknown'];

// DC priority-change switch. false = DC BLOCKED (default, safe). true = DC allowed.
// Controls BOTH screens (Live panel + Monitoring page), UI and checks.
// The backend has its own matching switch in central_server.py
// (ALLOW_DC_PRIORITY_CHANGE) - both must be true for DC to work.
export const ALLOW_DC_PRIORITY_CHANGE = false;

export function isOfflineStatus(status) {
  const s = (status || '').toLowerCase();
  return OFFLINE_STATUS_PATTERNS.some((p) => s.includes(p));
}

// ----------------------------------------------------------------------
// Priority conversion - ported from WanTable.jsx's proven logic, not
// reimplemented from scratch. The raw API convention genuinely differs
// by device type:
//   Fully Managed:    1=P1, 2=P2, 3=P3, 4=P4, 999=Disabled (1-based)
//   WAN Managed / DC: 0=P1, 1=P2, 2=P3, 3=P4, 999=Disabled (0-based)
// displayPriority is always normalized to 0-based (0=P1..3=P4, 999=Disabled)
// regardless of device type - that's what the UI shows and what the
// picker below works with.
// ----------------------------------------------------------------------

export function toDisplayPriority(rawPriority, isFullyManaged) {
  const n = Number(rawPriority);
  if (n === 999 || Number.isNaN(n)) return 999;

  if (isFullyManaged) {
    return n === 0 ? 999 : n - 1;
  }
  return n;
}

export function toRawPriority(displayPriority) {
  // Matches the formula legacy WanTable's updatePriorities() actually sends
  // to the /api/wan/priority endpoint (ic2Priority), which is uniform across
  // device types - NOT the unused convertDisplayToBackendPriority helper,
  // which only differs FM vs WM/DC and is never wired to the real API call.
  // IC2 API: 0 = disabled, 1 = P1, 2 = P2, 3 = P3, 4 = P4 (for every device type).
  if (displayPriority === 999 || Number.isNaN(displayPriority)) {
    return 0;
  }
  return displayPriority + 1;
}

// Inverse of toRawPriority - what display priority a given write-side
// (payload) raw value represents. Safe to use for any device type since
// the write convention itself is uniform.
export function writeRawToDisplay(rawWrite) {
  const n = Number(rawWrite);
  if (n === 0 || Number.isNaN(n)) return 999;
  return n - 1;
}

// Inverse of toDisplayPriority - the read-side raw value that would
// produce a given display priority for this device type. Needed because
// the read-side convention differs by device type (FM: 1-based, 999 for
// disabled; WM/DC: 0-based, disabled reported as "Disable"/NaN) while the
// write-side convention toRawPriority uses is uniform. Storing a
// write-side value directly into a WAN's `priority` field and later
// reading it back through toDisplayPriority (read-side) silently applies
// the wrong convention for non-FM devices - this is what caused a WM/DC
// device to show every WAN's priority off by one immediately after a
// change (before any fetch had even completed).
export function displayToReadRaw(displayPriority, isFullyManaged) {
  if (displayPriority === 999) return 999; // toDisplayPriority treats 999 as disabled for every device type
  return isFullyManaged ? displayPriority + 1 : displayPriority;
}

export function priorityLabel(displayPriority) {
  if (displayPriority === 999) return 'Disabled';
  return `Priority ${displayPriority + 1}${displayPriority === 0 ? ' (Highest)' : ''}`;
}

// ----------------------------------------------------------------------
// Pending-priority memory
// ----------------------------------------------------------------------
// Legacy WanTable.jsx *feels* like it remembers a priority change across
// closing and reopening the modal, but there's no localStorage/
// sessionStorage anywhere in it - its own local optimistic state
// (localWanData) is plain React state that resets on unmount. What
// actually survives reopen is the backend: per the real server log,
// /api/wan/priority synchronously patches the backend's own served
// report file/cache before the router's next real telemetry poll, so
// any fresh REST call already reflects the change. FM's fetchAllWanFM
// happens to hit that same patched endpoint (/api/router/.../wan/all),
// but WM/DC reads from a different live endpoint
// (/api/monitoring/all-data) with no guarantee it's patched the same
// way. Rather than depend on that, this keeps its own short-lived
// client-side memory of what was actually submitted per WAN, applies it
// on top of whatever a live fetch returns, and clears an entry once the
// fetch confirms it landed (or a safety-net expiry passes, so a
// genuinely-failed change doesn't get stuck showing the wrong thing
// forever).

export const PENDING_PRIORITY_TTL_MS = 5 * 60 * 1000; // 5 minutes - outlasts typical collector lag without masking a real failure indefinitely

export function pendingPriorityKey(scannerId) {
  // Versioned (v2: displayPriority-based) - a schema change here previously
  // left old v1 entries ({priority: <raw>}) in a real browser's
  // sessionStorage from earlier in the same session, which the new code
  // read as entry.displayPriority === undefined. That never matched
  // anything, so those entries never resolved and kept overriding with
  // NaN, which toDisplayPriority/toRawPriority both treat as "disabled" -
  // silently corrupting every subsequent priority change for those WANs.
  // Bumping the key guarantees old-schema data is never read as new-schema
  // data again, on top of the validation below.
  return `lwp_pending_priority_v2:${scannerId}`;
}

export function loadPendingPriorities(scannerId) {
  try {
    const raw = sessionStorage.getItem(pendingPriorityKey(scannerId));
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

export function savePendingPriorities(scannerId, pending) {
  try {
    sessionStorage.setItem(pendingPriorityKey(scannerId), JSON.stringify(pending));
  } catch {
    // sessionStorage unavailable/full - memory just won't survive a remount, not fatal
  }
}

export function recordPendingPriorities(scannerId, priorityPayload) {
  const pending = loadPendingPriorities(scannerId);
  const now = Date.now();
  for (const { wan_id, priority } of priorityPayload) {
    // Store the canonical display priority, not the write-side raw value -
    // see displayToReadRaw for why. writeRawToDisplay is safe here because
    // the write-side convention itself is uniform across device types.
    pending[String(wan_id)] = { displayPriority: writeRawToDisplay(priority), ts: now };
  }
  savePendingPriorities(scannerId, pending);
}

// Applies any still-pending overrides onto freshly-fetched WAN rows, and
// prunes an entry once the fetch confirms it landed (or it's expired).
export function applyPendingPriorities(scannerId, wans, wanIdOf, isFullyManaged) {
  const pending = loadPendingPriorities(scannerId);
  if (Object.keys(pending).length === 0) return wans;

  const now = Date.now();
  let dirty = false;

  const result = wans.map((wan) => {
    const id = String(wanIdOf(wan));
    const entry = pending[id];
    if (!entry) return wan;

    // Defensive: a malformed or unrecognized entry (wrong shape, non-finite
    // value, missing timestamp) is discarded outright rather than trusted -
    // this is what should have happened to old-schema entries in the first
    // place, and guards against the same class of bug if the schema ever
    // changes again.
    const validDp = entry.displayPriority === 999 || Number.isFinite(entry.displayPriority);
    if (!validDp || !Number.isFinite(entry.ts)) {
      delete pending[id];
      dirty = true;
      return wan;
    }

    if (now - entry.ts > PENDING_PRIORITY_TTL_MS) {
      delete pending[id];
      dirty = true;
      return wan;
    }
    if (toDisplayPriority(wan.priority, isFullyManaged) === entry.displayPriority) {
      // Backend has caught up - stop overriding this one.
      delete pending[id];
      dirty = true;
      return wan;
    }
    return { ...wan, priority: displayToReadRaw(entry.displayPriority, isFullyManaged) };
  });

  if (dirty) savePendingPriorities(scannerId, pending);
  return result;
}

// ----------------------------------------------------------------------
// Priority write payload - ONE builder for desktop drag and mobile picker
// ----------------------------------------------------------------------
// Mirrors legacy WanTable.updatePriorities() exactly: EVERY WAN in the
// payload (not just the one being changed) goes through
//   read-side raw -> display (device-type aware) -> write-side (uniform IC2)
// The previous code sent non-target WANs' READ-side raw value straight
// through as a WRITE-side value. On WAN Managed, read 0 = P1 but write
// 0 = DISABLED, so every untouched P1 WAN was silently disabled, and every
// untouched P2 (read 1) was promoted to P1 (write 1). Never send a raw
// read-side value to the API.

// Write-side values IC2 accepts: 0 = disabled, 1..4 = P1..P4.
export const VALID_WRITE_PRIORITIES = [0, 1, 2, 3, 4];

// wan_id for the payload, or null if this WAN must not be sent (FM
// secondary-router WANs - excluded exactly like legacy).
export function payloadWanId(wan, isFullyManaged) {
  if (!isFullyManaged) return wan.id;
  const idStr = String(wan.id).toLowerCase();
  if (idStr.startsWith('secondary')) return null;
  const match = idStr.match(/^primary_(\d+)$/);
  if (match) return parseInt(match[1], 10);
  const n = Number(wan.id);
  return Number.isFinite(n) ? n : wan.id;
}

export function wanName(wan) {
  return wan.name || wan.interface_name;
}

// Build the full payload from the FULL (unfiltered) WAN list. Only the
// target's display priority changes; every other WAN is re-sent at its
// current display priority, converted to write-side.
export function buildPriorityPayload(allWans, targetWan, newDisplayPriority, isFullyManaged) {
  return allWans
    .map((wan) => {
      const wan_id = payloadWanId(wan, isFullyManaged);
      if (wan_id === null || wan_id === undefined) return null;
      const isTarget = wanName(wan) === wanName(targetWan);
      const displayPriority = isTarget
        ? newDisplayPriority
        : toDisplayPriority(wan.priority, isFullyManaged);
      return { wan_id, priority: toRawPriority(displayPriority) };
    })
    .filter(Boolean);
}

// Independent check on a finished payload, BEFORE anything is sent.
// Returns an error string, or null if the payload is safe to send.
//   1. Every sendable WAN is present exactly once (no partial payloads).
//   2. Every value is a valid write-side value (0..4 - never 999 etc).
//   3. Every WAN except the target keeps its CURRENT display priority -
//      this is the guard that would have caught the original bug, and
//      catches any future read/write convention mix-up the same way.
export function validatePriorityPayload(payload, allWans, targetWan, newDisplayPriority, isFullyManaged) {
  const sendable = allWans.filter((wan) => {
    const id = payloadWanId(wan, isFullyManaged);
    return id !== null && id !== undefined;
  });
  if (sendable.length === 0) return 'No WAN interfaces found to update.';
  if (payload.length !== sendable.length) {
    return `Payload has ${payload.length} WANs but the router has ${sendable.length} - refusing a partial update.`;
  }

  for (const wan of sendable) {
    const id = String(payloadWanId(wan, isFullyManaged));
    const entries = payload.filter((p) => String(p.wan_id) === id);
    if (entries.length !== 1) return `WAN "${wanName(wan)}" appears ${entries.length} times in the payload.`;
    const { priority } = entries[0];
    if (!VALID_WRITE_PRIORITIES.includes(priority)) {
      return `WAN "${wanName(wan)}" has invalid write priority ${priority}.`;
    }
    const isTarget = wanName(wan) === wanName(targetWan);
    const expectedDisplay = isTarget ? newDisplayPriority : toDisplayPriority(wan.priority, isFullyManaged);
    if (writeRawToDisplay(priority) !== expectedDisplay) {
      return `WAN "${wanName(wan)}" would change from ${priorityLabel(toDisplayPriority(wan.priority, isFullyManaged))} to ${priorityLabel(writeRawToDisplay(priority))} - only "${wanName(targetWan)}" should change.`;
    }
  }
  return null;
}


// Short chip label, e.g. "P1" / "DISABLED" - from a DISPLAY priority.
export function shortPriorityLabel(displayPriority) {
  return displayPriority === 999 ? 'DISABLED' : `P${displayPriority + 1}`;
}

// Chip label for the Monitoring page's REPORT rows (/api/monitoring/*-data).
// Those rows come from the monitoring cache, which stores priority 0-based
// with disabled as 'Disable' for every device type (the same convention
// the backend's update_local_wan_priorities() writes). Display only -
// NEVER used to build a write payload.
export function reportPriorityLabel(rawReportPriority) {
  if (rawReportPriority === undefined || rawReportPriority === null || rawReportPriority === '—') return '—';
  return shortPriorityLabel(toDisplayPriority(rawReportPriority, false));
}

// Monitoring page: overlay the SAME pending-priority memory LiveWanPanel
// uses onto TODAY's report rows, so a change shows immediately (A P1,
// C P3, B P4) instead of the pre-change report until the collector
// catches up. Report rows use the 0-based / 'Disable' convention for every
// device type, hence isFullyManaged = false. Entries are pruned once the
// report confirms them, or after the 5-minute TTL - same rules as the
// live panel. DISPLAY ONLY: call it only for today's daily view, never
// for past dates / weekly / monthly, and never to build a payload.
export function applyPendingToReport(serialNumber, deviceData) {
  if (!deviceData || !serialNumber) return deviceData;
  const idOf = (wan) => wan.id;
  const arr = (x) => (Array.isArray(x) ? x : []);
  return {
    ...deviceData,
    wanInterfaces: applyPendingPriorities(serialNumber, arr(deviceData.wanInterfaces), idOf, false),
    cellularInterfaces: applyPendingPriorities(serialNumber, arr(deviceData.cellularInterfaces), idOf, false),
  };
}

export const isKnownManagementType = (t) => ['fully_managed', 'wan_managed', 'data_center'].includes(t);

export const canChangePriorityForType = (managementType) =>
  isKnownManagementType(managementType) && (managementType !== 'data_center' || ALLOW_DC_PRIORITY_CHANGE);

// wan_id used as the pending-memory key - identical to LiveWanPanel's
// original inline wanId() in refetch().
export function pendingWanIdOf(isFullyManaged) {
  return (wan) => {
    if (!isFullyManaged) return wan.id;
    const idStr = String(wan.id).toLowerCase();
    const match = idStr.match(/^primary_(\d+)$/);
    if (match) return parseInt(match[1], 10);
    const n = Number(wan.id);
    return Number.isFinite(n) ? n : wan.id;
  };
}

// Fresh WAN list for a device, exactly as LiveWanPanel loads it: the
// device-type-specific live fetch, with pending-priority memory applied.
export async function loadLiveWans(managementType, serialNumber, deviceName, scope = 'admin') {
  if (!isKnownManagementType(managementType)) throw new Error('Unknown device type - priority cannot be changed.');
  const isFM = managementType === 'fully_managed';
  const result = isFM
    ? await fetchAllWanFM(serialNumber, deviceName, scope)
    : await fetchAllWanManaged(serialNumber, scope);
  return applyPendingPriorities(serialNumber, result.wanConnections || [], pendingWanIdOf(isFM), isFM);
}

// ALL pre-send checks, in order. Nothing may be sent unless status === 'ok'.
//   1. Data Center hard block
//   2. Target must be a sendable (non-secondary) WAN
//   3. No-op
//   4. Payload build + validation (full coverage, valid values, only the target changes)
//   5. "Keep at least one connected WAN enabled" safety net
// Returns { status: 'ok' | 'noop' | 'refused', payload?, message?, validationError? }
export function checkPriorityChange(allWans, targetWan, newDisplayPriority, managementType) {
  const isFullyManaged = managementType === 'fully_managed';

  if (!isKnownManagementType(managementType)) {
    return { status: 'refused', message: 'Unknown device type - priority cannot be changed.' };
  }
  if (managementType === 'data_center' && !ALLOW_DC_PRIORITY_CHANGE) {
    return { status: 'refused', message: 'Priority changes are disabled for Data Center devices.' };
  }
  if (!targetWan || payloadWanId(targetWan, isFullyManaged) === null) {
    return { status: 'refused', message: 'Secondary router WAN priority cannot be changed.' };
  }
  if (toDisplayPriority(targetWan.priority, isFullyManaged) === newDisplayPriority) {
    return { status: 'noop' };
  }

  const payload = buildPriorityPayload(allWans, targetWan, newDisplayPriority, isFullyManaged);
  const payloadError = validatePriorityPayload(payload, allWans, targetWan, newDisplayPriority, isFullyManaged);
  if (payloadError) {
    return {
      status: 'refused',
      validationError: payloadError,
      payload,
      message: `Priority change refused - safety check failed:\n${payloadError}\n\nNothing was sent to the router.`,
    };
  }

  const wouldStayConnected = allWans.some((wan) => {
    const id = payloadWanId(wan, isFullyManaged);
    if (id === null || id === undefined) return false;
    const entry = payload.find((p) => String(p.wan_id) === String(id));
    if (!entry || entry.priority === 0) return false;
    const status = (wan.status || wan.status_text || '').toLowerCase();
    if (status.includes('disabled')) return false;
    return !isOfflineStatus(status);
  });
  if (!wouldStayConnected) {
    return {
      status: 'refused',
      message: 'This change would leave no connected WAN interface enabled, which could cut off remote access to this router. Change refused - adjust the priorities so at least one currently-connected WAN stays enabled.',
    };
  }

  return { status: 'ok', payload };
}
